import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import PixiEditorSimple from './PixiEditorSimple';
import { useNotification } from '../../../Helper/NotificationProvider';

// Integration component that replaces the React Konva editor
const EditorPixiIntegration = ({ 
  currentLocation, 
  props, 
  state, 
  setState, 
  selectedIds, 
  setSelectedIds,
  stageRef,
  bendMode,
  onApplyBend,
  onUpdateHistory,
  onContextMenu,
  isManualSelecting,
  onManualSelect,
  debugMode,
  bgLocked,
  // ... other props from original Editor
}) => {
  const { openNotification, contextHolder } = useNotification();
  const [selectionRect, setSelectionRect] = useState(null);
  const [showSelectionRect, setShowSelectionRect] = useState(false);
  
  // Extract state values
  const {
    backgroundImage,
    width,
    height,
    shapes,
    scanPaths,
    isLocked,
    pendingBackgroundImageChange,
  } = state;
  
  // Memoize first markers for performance
  const firstMarkers = useMemo(() => {
    const markers = new Set();
    scanPaths.forEach(path => {
      if (path.firstMarker) {
        markers.add(path.firstMarker);
      }
    });
    return markers;
  }, [scanPaths]);
  
  // Memoize selectedIds to prevent unnecessary re-renders
  const memoizedSelectedIds = useMemo(() => selectedIds, [selectedIds]);

  // Measure container and keep the editor sized to remaining screen space
  const containerRef = useRef(null);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const update = () => {
      const rect = el.getBoundingClientRect();
      const footerHeight = document.querySelector('.footer')?.getBoundingClientRect().height || 0;
      const width = Math.floor(rect.width);
      const height = Math.max(0, Math.floor(window.innerHeight - footerHeight - rect.top));
      setContainerSize({ width, height });
    };

    update();

    const ro = new ResizeObserver(() => update());
    ro.observe(el);
    window.addEventListener('resize', update);

    return () => {
      ro.disconnect();
      window.removeEventListener('resize', update);
    };
  }, []);
  
  // Handle shape selection
  const handleSelect = useCallback((shapeId, isToggle = false) => {
    // Manual scan-direction mode: route clicks to the manual selection handler
    // (append the chair to the current scan path) instead of normal selection.
    if (isManualSelecting && shapeId !== null && onManualSelect) {
      onManualSelect(shapeId);
      return;
    }
    if (shapeId === null) {
      setSelectedIds([]);
    } else if (props.viewOnly) {
      // In viewOnly mode select only the clicked chair (individual selection).
      // Selecting the whole scan-path group here breaks single-chair right-click
      // actions; the active scan path is already tracked separately via onShapeSelect.
      setSelectedIds([shapeId]);
    } else if (isToggle) {
      // Toggle selection for multi-select
      setSelectedIds(prev => {
        if (prev.includes(shapeId)) {
          return prev.filter(id => id !== shapeId);
        } else {
          return [...prev, shapeId];
        }
      });
    } else {
      // Single selection
      setSelectedIds([shapeId]);
    }
    
    if (props.onShapeSelect) {
      props.onShapeSelect(shapeId);
    }
  }, [setSelectedIds, props.onShapeSelect, props.viewOnly, isManualSelecting, onManualSelect]);
  
  // Handle context menu
  const handleShapeContextMenu = useCallback((shapeId, e) => {
    if (onContextMenu) {
      onContextMenu(shapeId, e);
    }
  }, [onContextMenu]);
  
  // Handle shape changes (dragging, etc.)
  const handleShapeChange = useCallback((shapeId, newAttrs) => {
    setState(prev => {
      const updatedShapes = prev.shapes.map(shape =>
        shape.id === shapeId ? { ...shape, ...newAttrs } : shape
      );
      return {
        ...prev,
        shapes: updatedShapes,
        isDirty: true
      };
    });
  }, [setState]);
  
  // Commit bg image transform to history after a resize/drag ends.
  const handleBgTransformCommit = useCallback((newBgTransform) => {
    if (onUpdateHistory) {
      onUpdateHistory(shapes, scanPaths, newBgTransform);
    }
  }, [onUpdateHistory, shapes, scanPaths]);

  // Commit shape positions to undo/redo history after a drag or resize ends.
  // positionUpdates is an array of { id, x, y, width?, height?, rotation? } for changed shapes only.
  const handleCommitShapes = useCallback((positionUpdates) => {
    if (!onUpdateHistory) return;
    setState(prev => {
      const updatedShapes = prev.shapes.map(shape => {
        const update = positionUpdates.find(u => u.id === shape.id);
        if (!update) return shape;
        const s = { ...shape, x: update.x, y: update.y };
        if (update.width    !== undefined) s.width    = update.width;
        if (update.height   !== undefined) s.height   = update.height;
        if (update.rotation !== undefined) s.rotation = update.rotation;
        return s;
      });
      // Call updateHistory with the fully-merged shapes array
      onUpdateHistory(updatedShapes, prev.scanPaths);
      return prev; // setState already happened in handleUpdateShapePositions
    });
  }, [setState, onUpdateHistory]);

  // Handle multiple shape position updates (for dragging multiple shapes)
  const handleUpdateShapePositions = useCallback((positionUpdates) => {
    setState(prev => {
      const updatedShapes = prev.shapes.map(shape => {
        const update = positionUpdates.find(update => update.id === shape.id);
        if (update) {
          const updatedShape = { ...shape, x: update.x, y: update.y };

          // Also update width/height/rotation if provided (for resize/rotate operations)
          if (update.width !== undefined) {
            updatedShape.width = update.width;
          }
          if (update.height !== undefined) {
            updatedShape.height = update.height;
          }
          if (update.rotation !== undefined) {
            updatedShape.rotation = update.rotation;
          }

          return updatedShape;
        }
        return shape;
      });

      return {
        ...prev,
        shapes: updatedShapes,
        isDirty: true
      };
    });
  }, [setState]);
  
  // Handle multi-selection with rectangle
  const handleMultiSelect = useCallback((selectedShapeIds) => {
    // Update parent component's state (this is the main selectedIds state)
    setSelectedIds(selectedShapeIds);
    
    // Note: We don't call props.onShapeSelect for multi-selection
    // because it might interfere with the multi-selection state
    // The parent component should handle the selectedIds state directly
  }, [setSelectedIds]);
  
  // Show all shapes to avoid accidental clipping/cutting
  const visibleShapes = useMemo(() => shapes, [shapes]);

  // Initial bg image transform — restored from saved data or defaulted to natural image size.
  // A pending (unsaved) upload always uses the new image's own natural size/aspect ratio —
  // the previous image's saved bgX/bgY/bgW/bgH belong to a different image and must not be reused.
  const bgImageInitialTransform = useMemo(() => {
    if (!backgroundImage) return null;
    const bi = pendingBackgroundImageChange ? undefined : currentLocation?.backgroundImage;
    if (bi?.bgX !== undefined) {
      return { x: bi.bgX, y: bi.bgY, w: bi.bgW ?? width, h: bi.bgH ?? height, rot: bi.bgRot ?? 0 };
    }
    return { x: 0, y: 0, w: width, h: height, rot: 0 };
  }, [backgroundImage, currentLocation, width, height, pendingBackgroundImageChange]);
  
  // Use the simple PixiJS editor
  const EditorComponent = PixiEditorSimple;
  
  return (
    <>
      {contextHolder}
      
      
      {/* Main Editor */}
      <div
        ref={containerRef}
        style={{ width: '100%', height: containerSize.height ? `${containerSize.height}px` : '100vh', minHeight: 0 }}
      >
        <EditorComponent
          ref={stageRef}
          shapes={visibleShapes}
          scanPaths={scanPaths}
          backgroundImage={backgroundImage}
          bgImageInitialTransform={bgImageInitialTransform}
          width={containerSize.width || width}
          height={containerSize.height || height}
          selectedIds={memoizedSelectedIds}
          firstMarkers={firstMarkers}
          selectedMarker={props.selectedMarker}
          guestList={props.guestList}
          onSelect={handleSelect}
          onContextMenu={handleShapeContextMenu}
          isLocked={isLocked}
          viewOnly={props.viewOnly}
          currentScanPathId={props?.currentScanPathId}
          onShapeChange={handleShapeChange}
          selectionRect={selectionRect}
          showSelectionRect={showSelectionRect}
          onMultiSelect={handleMultiSelect}
          onUpdateShapePositions={handleUpdateShapePositions}
          onCommitShapes={handleCommitShapes}
          onBgTransformCommit={handleBgTransformCommit}
          bendMode={bendMode}
          onApplyBend={onApplyBend}
          debugMode={debugMode}
          bgLocked={bgLocked}
        />
      </div>
    </>
  );
};

export default EditorPixiIntegration;
